import { useTvtZone } from "../hooks/useTvtZone.js";

/**
 * Live §F.4 Price-Band Zone indicator. Renders one of three pills:
 *   - Premium  (gold)    — market price > intrinsic; treasury selling
 *   - Neutral  (slate)   — within ±10% of intrinsic; no execution
 *   - Discount (emerald) — market price < intrinsic; treasury buying
 *
 * Falls back to a muted "Calibrating" pill when the backend reports a
 * benign revert (IntrinsicValueUnavailable / NoObservation, i.e. the
 * TWAP window hasn't elapsed yet on a fresh deploy).
 */
export default function ZoneIndicator({ compact = false }) {
    const { data, isLoading } = useTvtZone();
    const zone = data?.zone;
    const palette = TONE[zone] || TONE.Calibrating;
    const label = zone || (isLoading ? "—" : "Calibrating");

    return (
        <span
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 ${palette.bg} ${palette.ring} ring-1`}
            title={
                data?.spotPrice
                    ? `Spot ${formatPrice(data.spotPrice)} · Intrinsic ${formatPrice(data.intrinsicValuePerGil)}`
                    : "Awaiting first executor observation"
            }
        >
            <span className={`h-2 w-2 rounded-full ${palette.dot}`} aria-hidden="true" />
            <span className={`font-manrope font-semibold ${palette.text} ${compact ? "text-[10px]" : "text-[11px]"} uppercase tracking-widest`}>
                {compact ? label : `Zone · ${label}`}
            </span>
        </span>
    );
}

const TONE = {
    Premium: {
        bg: "bg-[#FBC206]/15",
        ring: "ring-[#FBC206]/50",
        dot: "bg-[#FBC206]",
        text: "text-[#b98c23]"
    },
    Neutral: {
        bg: "bg-[#102712]/[0.06]",
        ring: "ring-[#102712]/20",
        dot: "bg-[#102712]/70",
        text: "text-[#102712]"
    },
    Discount: {
        bg: "bg-[#22C55E]/15",
        ring: "ring-[#22C55E]/50",
        dot: "bg-[#22C55E]",
        text: "text-[#2e7a3a]"
    },
    Calibrating: {
        bg: "bg-[#102712]/[0.05]",
        ring: "ring-[#102712]/15",
        dot: "bg-[#102712]/40",
        text: "text-[#102712]/70"
    }
};

function formatPrice(value) {
    if (value == null) return "—";
    const n = Number(value) / 1e20;
    if (!Number.isFinite(n) || n <= 0) return "—";
    return `$${n.toFixed(6)}`;
}
